import React, { useState } from 'react';
import { Wrench, Home, Stethoscope, Zap, HardHat, Shield } from 'lucide-react';

const GhlUseCases: React.FC = () => {
  const [activeTab, setActiveTab] = useState('contractors');

  const tabs = [
    { id: 'contractors', label: 'Contractors', icon: HardHat },
    { id: 'realestate', label: 'Real Estate', icon: Home },
    { id: 'dental', label: 'Dental/Medical', icon: Stethoscope },
    { id: 'trades', label: 'Trades', icon: Wrench },
    { id: 'insurance', label: 'Insurance', icon: Shield },
  ];

  const content: Record<string, {
    title: string;
    scenario: string;
    search: string;
    workflow: string;
    result: string;
  }> = {
    contractors: {
      title: "GHL Agency Serving Contractors",
      scenario: "You run a GHL agency managing marketing for HVAC, roofing, and general contractors. Your clients need more leads, but first YOU need to fill their pipeline.",
      search: "Search: 'General Contractors' in 'Dallas, TX' — Export 2,400 verified contacts with emails and phone numbers.",
      workflow: "Import to GHL sub-account. Trigger your 'New Contractor Outreach' snapshot with SMS + email drip sequence.",
      result: "Your contractor client gets 15-20 new project inquiries per month from verified, licensed peers for referral partnerships and subcontractor networks."
    },
    realestate: {
      title: "Real Estate Coaching Programs",
      scenario: "You sell coaching and training programs to real estate agents. You need a constant flow of newly licensed agents who need mentorship.",
      search: "Search: 'Real Estate' in 'Florida' — Filter by license date within last 90 days. Export 800+ newly licensed agents.",
      workflow: "Import to GHL. Tag as 'New Agent'. Trigger your welcome email sequence with free webinar invite and coaching pitch.",
      result: "20% open rate, 5% book a call. That is 40 discovery calls from one export. At $297/mo coaching, even 5 conversions = $1,485/mo recurring."
    },
    dental: {
      title: "Dental Marketing Agency",
      scenario: "You manage Google Ads and GHL automations for dental practices. You want to pitch your services directly to dentists.",
      search: "Search: 'Dentist' in 'Phoenix, AZ' — 1,200 licensed dentists with practice addresses and contact info.",
      workflow: "Import to GHL pipeline. Send personalized cold email: 'Dr. [Last Name], I noticed your practice at [Address]...' followed by SMS drip.",
      result: "5 new dental clients per month at $1,500/mo retainer. ProGeoData pays for itself in the first campaign."
    },
    trades: {
      title: "Trade Services Marketers",
      scenario: "You market digital services to plumbers, electricians, and HVAC techs. They need websites, Google Business Profile optimization, and lead gen.",
      search: "Search: 'Plumber' in ZIP code '75001' — 340 verified plumbing contractors. Or go statewide: 'Electrician' in 'Texas' — 4,200+ records.",
      workflow: "Import to GHL. Segment by city. Run localized SMS campaigns: 'Hey [First Name], are you getting enough leads from Google in [City]?'",
      result: "Trade professionals respond to direct, localized outreach. 8-12% reply rate on SMS. Close 3-5 clients per batch."
    },
    insurance: {
      title: "Insurance Agency Builders",
      scenario: "You recruit independent insurance agents or sell leads to insurance agencies. You need to reach licensed producers in specific states.",
      search: "Search: 'Insurance' in 'California' — Filter by active license status. 3,500+ verified insurance producers.",
      workflow: "Import to GHL. Tag by license line (Life, Health, P&C). Run targeted email: 'Licensed P&C agents in CA — we have exclusive homeowner leads.'",
      result: "Insurance agents are always looking for quality leads. Convert 2% to your program at $500/mo = $35,000/mo revenue from one state."
    }
  };

  const active = content[activeTab];

  return (
    <section id="use-cases" className="py-24 bg-slate-950">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className="text-3xl font-bold text-white text-center mb-4">GHL Campaign Playbooks</h2>
        <p className="text-slate-400 text-center mb-12 max-w-2xl mx-auto">
          Real scenarios showing how GHL marketers use ProGeoData to fill pipelines and close deals.
        </p>

        <div className="flex flex-wrap justify-center gap-3 mb-12">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center px-5 py-2.5 rounded-full text-sm font-bold transition-all ${
                  activeTab === tab.id
                    ? 'bg-emerald-600 text-white shadow-lg shadow-emerald-900/20'
                    : 'bg-slate-900 text-slate-400 hover:bg-slate-800'
                }`}
              >
                <Icon className="w-4 h-4 mr-2" />
                {tab.label}
              </button>
            );
          })}
        </div>

        <div className="max-w-4xl mx-auto bg-slate-900 rounded-2xl p-8 md:p-12 border border-slate-800">
          <h3 className="text-2xl font-bold text-white mb-6">{active.title}</h3>

          <div className="space-y-6">
            <div>
              <div className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Scenario</div>
              <p className="text-slate-400 leading-relaxed">{active.scenario}</p>
            </div>

            <div className="bg-slate-950 rounded-xl p-6 border border-slate-800">
              <div className="text-xs font-bold text-emerald-400 uppercase tracking-wider mb-2">ProGeoData Search</div>
              <p className="text-slate-300 leading-relaxed font-mono text-sm">{active.search}</p>
            </div>

            <div className="bg-slate-950 rounded-xl p-6 border border-slate-800">
              <div className="text-xs font-bold text-sky-400 uppercase tracking-wider mb-2">GHL Workflow</div>
              <p className="text-slate-300 leading-relaxed">{active.workflow}</p>
            </div>

            <div className="bg-gradient-to-r from-emerald-900/20 to-sky-900/20 rounded-xl p-6 border border-emerald-500/20">
              <div className="text-xs font-bold text-emerald-400 uppercase tracking-wider mb-2">Result</div>
              <p className="text-white leading-relaxed font-medium">{active.result}</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default GhlUseCases;
